/**
 * Request deduplication utility with short-lived result caching.
 *
 * Prevents duplicate API calls in two scenarios:
 * 1. Simultaneous calls — multiple components triggering the same
 *    API at once share a single in-flight promise.
 * 2. Sequential calls — calls made within a short TTL window after
 *    a previous identical call receive the cached result instantly.
 */

interface CacheEntry {
  promise: Promise<any>;
  result?: any;
  expiresAt: number;
  timeoutId?: ReturnType<typeof setTimeout>;
}

const cache = new Map<string, CacheEntry>();

const CACHE_TTL_MS = 5000; // cache results for 5 seconds

/**
 * Wraps an async function so that concurrent or near-concurrent
 * calls with the same arguments share a single network request.
 *
 * @param keyPrefix - A unique prefix for this endpoint (e.g. 'in-feed-courses')
 * @param fn        - The original async function to deduplicate
 * @returns         - A wrapper with the same signature that deduplicates calls
 */
const deduplicateRequest = <T extends (...args: any[]) => Promise<any>>(
  keyPrefix: string,
  fn: T
): T => {
  const wrapper = ((...args: any[]) => {
    const cacheKey = `${keyPrefix}:${JSON.stringify(args)}`;

    const existing = cache.get(cacheKey);
    if (existing && Date.now() < existing.expiresAt) {
      // Return the in-flight promise or a resolved one with cached result
      return existing.promise;
    }

    if (existing) {
      clearTimeout(existing.timeoutId);
      cache.delete(cacheKey);
    }

    const scheduleCleanup = (trackedPromise: Promise<any>) =>
      setTimeout(() => {
        const current = cache.get(cacheKey);
        if (current && current.promise === trackedPromise) {
          cache.delete(cacheKey);
        }
      }, CACHE_TTL_MS);

    const promise = fn(...args).then(
      (result: any) => {
        // Store the resolved result and extend the TTL
        const entry = cache.get(cacheKey);
        if (entry && entry.promise === promise) {
          entry.result = result;
          entry.expiresAt = Date.now() + CACHE_TTL_MS;
          clearTimeout(entry.timeoutId);
          entry.timeoutId = scheduleCleanup(promise);
        }
        return result;
      },
      (error: any) => {
        // On error, remove from cache immediately so retries work
        const entry = cache.get(cacheKey);
        if (entry && entry.promise === promise) {
          clearTimeout(entry.timeoutId);
          cache.delete(cacheKey);
        }
        throw error;
      }
    );

    cache.set(cacheKey, {
      promise,
      expiresAt: Date.now() + CACHE_TTL_MS,
      timeoutId: scheduleCleanup(promise),
    });

    return promise;
  }) as unknown as T;

  return wrapper;
};

export default deduplicateRequest;
